import { toast } from '@/hooks/use-toast'
import type { Data, Layout, Config } from 'plotly.js'
import { isRecord } from './chart-data-helpers'

/** Type guard: true if value is a valid array of Plotly trace data (each element is a plain object with a type). */
export function isDataArray(value: unknown): value is Data[] {
  if (!Array.isArray(value)) return false
  return value.some((item) => isRecord(item) && 'type' in item && item.type != null)
}

// =============================================================================
// Single place: layout/config overrides for chart display. Do not add
// responsive/autosize/displayModeBar etc. anywhere else.
//
// Plotly `config` reference: https://plotly.com/javascript/configuration-options/
// (source: https://github.com/plotly/plotly.js/blob/master/src/plot_api/plot_config.js)
// =============================================================================

/** Minimum height (px) for grid/multi-plot charts so they don't render too short. */
const MIN_HEIGHT_GRID = 650

/** Compact chart font sizes (used in narrow containers). */
export const COMPACT_FONT_SIZE = 11
export const COMPACT_TITLE_FONT_SIZE = 14

/** Strip axis.matches to avoid Plotly "ignored xaxis.matches / yaxis.matches" infinite-loop warning. */
function stripAxisMatches(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(rest)) {
    if (key.match(/^(xaxis|yaxis)(\d*)\.matches$/)) continue
    const value = rest[key]
    if (key.match(/^(xaxis|yaxis)(\d*)$/) && isRecord(value) && 'matches' in value) {
      const { matches: _m, ...axisRest } = value
      out[key] = axisRest
    } else {
      out[key] = value
    }
  }
  return out
}

/** True if layout looks like a grid/multi-plot chart (grouped/stacked bars, or multiple y-axes). */
function isGridLikeChart(layout: Record<string, unknown>): boolean {
  const barmode = layout.barmode
  if (barmode === 'group' || barmode === 'stack' || barmode === 'overlay') return true
  const yAxisKeys = Object.keys(layout).filter((k) => /^yaxis\d*$/.test(k) && k !== 'yaxis')
  if (yAxisKeys.length > 0) return true
  return false
}

const DEFAULT_CHART_HEIGHT = 400

/** Returns chart title or fallback when empty. Use for add-to-chat and context badges. */
export function getChartTitleOrFallback(title?: string): string {
  return title?.trim() || 'Untitled Chart'
}

/** Returns the display height (px) that will be used for this chart layout. Use for skeletons/slots to avoid layout shift. */
export function getChartDisplayHeight(layout: Partial<Layout> | undefined): number {
  if (!layout || typeof layout !== 'object') return DEFAULT_CHART_HEIGHT
  const { height, ...rest } = layout
  const cleaned = stripAxisMatches(rest as Record<string, unknown>)
  const numericHeight = typeof height === 'number' ? height : undefined
  const isGrid = isGridLikeChart(cleaned)
  if (isGrid && (numericHeight === undefined || numericHeight < MIN_HEIGHT_GRID))
    return MIN_HEIGHT_GRID
  return numericHeight ?? DEFAULT_CHART_HEIGHT
}

/** Layout with display overrides (responsive, autosize). Plotly accepts these at runtime. */
export function getDisplayLayout(layout: Partial<Layout> | undefined): Partial<Layout> {
  const { width, margin, height, ...rest } = layout || {}
  const cleaned = stripAxisMatches(rest as Record<string, unknown>)
  const layoutWithoutAutoMargin: Record<string, unknown> = { ...cleaned }

  // Disable auto-margin on all axes to avoid Plotly "Too many auto-margin redraws" warnings
  for (const key of Object.keys(layoutWithoutAutoMargin)) {
    if (/^(xaxis|yaxis)\d*$/.test(key) && isRecord(layoutWithoutAutoMargin[key])) {
      const axis = layoutWithoutAutoMargin[key] as Record<string, unknown>
      const { automargin: _ignoredAutoMargin, ...axisRest } = axis
      layoutWithoutAutoMargin[key] = { ...axisRest, automargin: false }
    }
  }

  const numericHeight = typeof height === 'number' ? height : undefined
  const isGrid = isGridLikeChart(layoutWithoutAutoMargin)
  const effectiveHeight =
    isGrid && (numericHeight === undefined || numericHeight < MIN_HEIGHT_GRID)
      ? MIN_HEIGHT_GRID
      : numericHeight ?? DEFAULT_CHART_HEIGHT

  return {
    ...layoutWithoutAutoMargin,
    autosize: true,
    height: effectiveHeight,
  }
}

export function getDisplayConfig(config: Partial<Config> | undefined): Partial<Config> {
  return {
    ...config,
    displayModeBar: false,
    showTips: false,
    responsive: true,
    // Disable wheel zoom so Plotly does not attach non-passive wheel listeners;
    // keeps page scroll responsive and avoids "wheel event delayed" violations.
    scrollZoom: false,
  }
}

type PlotlyInstance = typeof import('plotly.js-dist-min')

// Plotly instance helpers
function isPlotlyInstance(obj: unknown): obj is PlotlyInstance {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  const relayout = Object.getOwnPropertyDescriptor(obj, 'relayout')?.value
  const downloadImage = Object.getOwnPropertyDescriptor(obj, 'downloadImage')?.value
  const toImage = Object.getOwnPropertyDescriptor(obj, 'toImage')?.value
  return (
    typeof relayout === 'function' &&
    (downloadImage === undefined || typeof downloadImage === 'function') &&
    (toImage === undefined || typeof toImage === 'function')
  )
}

function getPlotlyFromWindow(): PlotlyInstance | undefined {
  const plotly = (window as { Plotly?: PlotlyInstance | Record<string, unknown> }).Plotly
  if (!plotly) return undefined

  if (isPlotlyInstance(plotly)) {
    return plotly
  }

  // Some bundlers place the actual Plotly implementation on a default export
  const maybeDefault =
    (plotly as { default?: PlotlyInstance | Record<string, unknown> }).default ?? null
  if (maybeDefault && isPlotlyInstance(maybeDefault)) {
    return maybeDefault
  }

  // Fall back to using whatever is on window.Plotly even if the strict shape check fails.
  // This avoids breaking chart controls when Plotly's surface API shape changes.
  return plotly as PlotlyInstance
}

export function getPlotlyInstance(containerRef: React.RefObject<HTMLDivElement>): {
  Plotly: PlotlyInstance
  graphDiv: HTMLElement
} {
  const graphDiv = containerRef.current?.querySelector<HTMLElement>('.js-plotly-plot')
  if (!graphDiv) {
    throw new Error('Chart element not found')
  }

  const Plotly = getPlotlyFromWindow()
  if (!Plotly) {
    throw new Error('Plotly not available')
  }
  return { Plotly, graphDiv }
}

export function getCurrentLayout(graphDiv: HTMLElement): Record<string, unknown> {
  const layout = graphDiv.layout
  return isRecord(layout) ? layout : {}
}

// Zoom and interaction helpers
function toTimestamp(value: unknown): number | null {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return isNaN(parsed) ? null : parsed
  }
  return null
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? null : parsed
  }
  return null
}

function isValidNumber(value: number | null): value is number {
  return value !== null && !isNaN(value) && isFinite(value)
}

export function processAxisRanges(
  currentLayout: Record<string, unknown>,
  zoomFactor: number,
): Record<string, unknown> {
  const updateLayout: Record<string, unknown> = {}

  // Get all axis keys (xaxis, yaxis, xaxis2, yaxis2, etc.)
  const axisKeys = new Set<string>()

  // First, collect all axis keys from the layout
  for (const key of Object.keys(currentLayout)) {
    if (key.startsWith('xaxis') || key.startsWith('yaxis')) {
      // Handle both direct axis objects and dotted notation
      if (key.match(/^(xaxis|yaxis)(\d*)$/)) {
        axisKeys.add(key)
      }
    }
  }

  // Also check for dotted notation keys (e.g., 'xaxis.range', 'yaxis2.range')
  for (const key of Object.keys(currentLayout)) {
    const match = key.match(/^(xaxis\d*)\.|^(yaxis\d*)\./)
    if (match) {
      const axisKey = match[1] || match[2]
      if (axisKey) {
        axisKeys.add(axisKey)
      }
    }
  }

  // Process each axis
  for (const axisKey of axisKeys) {
    // Try to get axis layout from direct key first
    let axisLayout = currentLayout[axisKey]

    // If not found, try to reconstruct from dotted notation
    if (!isRecord(axisLayout)) {
      const built: Record<string, unknown> = {}
      for (const key of Object.keys(currentLayout)) {
        if (key.startsWith(`${axisKey}.`)) {
          const subKey = key.replace(`${axisKey}.`, '')
          built[subKey] = currentLayout[key]
        }
      }
      axisLayout = built
    }

    if (!isRecord(axisLayout)) continue

    // Check if range exists - can be in axisLayout or as dotted notation
    let range = axisLayout.range
    if (!Array.isArray(range)) {
      const rangeKey = `${axisKey}.range`
      const rangeValue = currentLayout[rangeKey]
      if (Array.isArray(rangeValue)) {
        range = rangeValue
      } else {
        // Try to get from autorange - if autorange is true, we need to get current range from Plotly
        continue
      }
    }

    if (!Array.isArray(range) || range.length !== 2) continue

    // Validate range values are not null/undefined
    if (range[0] == null || range[1] == null) continue

    const [minRaw, maxRaw] = range
    const axisType = axisLayout.type || currentLayout[`${axisKey}.type`]
    const isDateAxis = axisType === 'date' || axisType === '-'

    const min = isDateAxis ? toTimestamp(minRaw) : toNumber(minRaw)
    const max = isDateAxis ? toTimestamp(maxRaw) : toNumber(maxRaw)

    if (isValidNumber(min) && isValidNumber(max) && min !== max) {
      const center = (min + max) / 2
      const span = (max - min) * zoomFactor
      const newMin = center - span / 2
      const newMax = center + span / 2

      // Validate the calculated range is valid
      if (isValidNumber(newMin) && isValidNumber(newMax) && newMin !== newMax) {
        updateLayout[`${axisKey}.range`] = [newMin, newMax]
        updateLayout[`${axisKey}.autorange`] = false
      }
    }
  }

  const scene = currentLayout.scene
  if (isRecord(scene)) {
    for (const axisName of ['xaxis', 'yaxis', 'zaxis']) {
      const axis = scene[axisName]
      if (!isRecord(axis)) continue

      const range = axis.range
      if (!Array.isArray(range) || range.length !== 2) continue

      // Validate range values are not null/undefined
      if (range[0] == null || range[1] == null) continue

      const min = toNumber(range[0])
      const max = toNumber(range[1])

      if (isValidNumber(min) && isValidNumber(max) && min !== max) {
        const center = (min + max) / 2
        const span = (max - min) * zoomFactor
        const newMin = center - span / 2
        const newMax = center + span / 2

        // Validate the calculated range is valid
        if (isValidNumber(newMin) && isValidNumber(newMax) && newMin !== newMax) {
          updateLayout[`scene.${axisName}.range`] = [newMin, newMax]
          updateLayout[`scene.${axisName}.autorange`] = false
        }
      }
    }
  }

  return updateLayout
}

export async function withPlotlyErrorHandling<T>(
  operation: () => T | Promise<T>,
  errorMessage: string,
): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    toast({
      title: errorMessage,
      variant: 'destructive',
    })
    throw error
  }
}

export type ImageFormat = 'png' | 'jpeg' | 'svg' | 'webp'

export async function downloadChartAsImage(
  graphDiv: HTMLElement,
  Plotly: PlotlyInstance,
  format: ImageFormat,
  title?: string,
  originalLayout?: Partial<Layout>,
  onLoadingChange?: (loading: boolean) => void,
): Promise<void> {
  // Snapshot the pre-download layout so we can restore *exactly* what the user was viewing.
  // Some charts (especially in fullscreen) can end up with a bad width after a relayout-based
  // download flow unless we restore the original layout object.
  const preDownloadLayout = getCurrentLayout(graphDiv)
  const currentLayout = preDownloadLayout

  // Get original height - prioritize originalLayout if available
  const originalHeight =
    (originalLayout?.height !== undefined ? originalLayout.height : currentLayout.height) ??
    currentLayout.height

  // Build download layout: include title and original margins
  const downloadLayout: Record<string, unknown> = {
    ...currentLayout,
    ...(originalHeight !== undefined ? { height: originalHeight } : {}),
  }

  if (originalLayout?.title) {
    downloadLayout.title = originalLayout.title
  } else if (title) {
    downloadLayout.title = typeof title === 'string' ? { text: title } : title
  }

  if (originalLayout?.margin) {
    downloadLayout.margin = originalLayout.margin
  }

  const needsLayoutUpdate = Boolean(
    (originalLayout && (originalLayout.title || originalLayout.margin)) || title,
  )

  // Helper to download image
  const downloadImage = async (plotlyInstance: PlotlyInstance): Promise<void> => {
    if (plotlyInstance.toImage) {
      const dataUrl = await plotlyInstance.toImage(graphDiv, {
        format,
        height: graphDiv.offsetHeight,
        width: graphDiv.offsetWidth,
      })
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = `${title || 'chart'}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }

    if (plotlyInstance.downloadImage) {
      await plotlyInstance.downloadImage(graphDiv, {
        format,
        filename: title || 'chart',
        height: graphDiv.offsetHeight,
        width: graphDiv.offsetWidth,
      })
      return
    }

    throw new Error('Plotly.toImage or Plotly.downloadImage not available')
  }

  onLoadingChange?.(true)
  let didRelayoutForDownload = false

  try {
    // Apply download layout if needed
    if (needsLayoutUpdate) {
      didRelayoutForDownload = true
      Plotly.relayout(graphDiv, downloadLayout)
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // Try to download with current Plotly instance
    await downloadImage(Plotly)
  } finally {
    // Only restore if we changed the layout for the download. Restoring unconditionally can
    // introduce width drift/overflow for some charts.
    if (didRelayoutForDownload) {
      Plotly.relayout(graphDiv, preDownloadLayout)
    }
    onLoadingChange?.(false)
  }
}
